import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { AffectedCampaign } from "./_senderDisconnectChecks";

interface SenderDisconnectConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Email address of the sender being disconnected, for display only. */
  senderEmail: string;
  /** Campaigns that will be paused on confirm. May be empty (then dialog usually isn't shown at all). */
  affectedCampaigns: AffectedCampaign[];
  /** Optional extra paragraph (e.g. calendar-sync loss for CalendarSettings). */
  extraWarning?: React.ReactNode;
  /** Async confirm handler. Parent is responsible for closing the dialog. */
  onConfirm: () => void | Promise<void>;
  /** Pending state — disables buttons + shows spinner. */
  isProcessing?: boolean;
}

/**
 * Pre-flight confirmation dialog for disconnecting an email sender.
 *
 * One path only: "Disconnect and pause affected campaigns" + "Cancel".
 * Lists every active/paused Hermes campaign that would break, so the user can
 * see the blast radius before confirming.
 */
export function SenderDisconnectConfirmDialog({
  open,
  onOpenChange,
  senderEmail,
  affectedCampaigns,
  extraWarning,
  onConfirm,
  isProcessing = false,
}: SenderDisconnectConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const count = affectedCampaigns.length;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {senderEmail}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>
                    {count} {count === 1 ? "campaign is" : "campaigns are"} currently using this sender.
                  </strong>{" "}
                  Disconnecting will pause {count === 1 ? "it" : "them"} so remaining emails don't fail.
                  You can resume after reconnecting the same account.
                </AlertDescription>
              </Alert>

              <div className="rounded-md border max-h-64 overflow-y-auto">
                <ul className="divide-y">
                  {affectedCampaigns.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{c.name}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant={c.status === "active" ? "default" : "secondary"}
                          className="capitalize"
                        >
                          {c.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {c.emails_pending ?? 0} pending
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {extraWarning}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isProcessing}
            variant="destructive"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              "Disconnect and pause campaigns"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
